<template>
    <v-container fluid>
        <v-card class="pa-4">
            <!-- Заголовок -->
            <v-card-title class="text-h5 mb-4 d-flex flex-column align-start">
                <span>Акт сверки</span>
                <span>Расчёты с {{ formatDate(dateFrom) }} по {{ formatDate(dateTo) }}</span>
                <span>между {{ serviceProducerName }} и {{ logisticCompanyName }}</span>
            </v-card-title>

            <!-- Информация о сальдо -->
            <v-row class="px-3 mb-4">
                <v-col cols="12">
                    <v-alert type="info" dense text class="mb-0">
                        <strong>Сальдо на начало периода:</strong> {{ formatNumber(saldoBeg) }} ₽
                    </v-alert>
                </v-col>
            </v-row>

            <!-- Таблица -->
            <v-data-table 
                :headers="headers" 
                :items="verificationResultsDetails" 
                :loading="loading"
                loading-text="Загрузка данных..." 
                no-data-text="Нет данных..." 
                class="elevation-1" 
                :items-per-page="-1"
                :footer-props="{
                    'items-per-page-options': [-1, 50, 20, 15, 10],
                    'items-per-page-text': 'Записей на странице:'
                }">
                
                <!-- Форматирование даты начисления -->
                <template v-slot:[`item.accrualDate`]="{ item }">
                    {{ formatDate(item.accrualDate) }}
                </template>

                <!-- Форматирование даты платежа -->
                <template v-slot:[`item.paymentOrderDate`]="{ item }">
                    {{ formatDate(item.paymentOrderDate) }}
                </template>

                <!-- Форматирование суммы начисления -->
                <template v-slot:[`item.summa`]="{ item }">
                    <span class="font-weight-medium">
                        {{ item.summa && Number(item.summa) !== 0 ? formatNumber(item.summa) : '' }}
                    </span>
                </template>

                <!-- Форматирование суммы платежа -->
                <template v-slot:[`item.paymentOrderAmount`]="{ item }">
                    <span class="font-weight-medium">
                        {{ item.paymentOrderAmount && Number(item.paymentOrderAmount) !== 0 ?
                            formatNumber(item.paymentOrderAmount) : '' }}
                    </span>
                </template>

                <!-- Комментарий с ограничением длины -->
                <template v-slot:[`item.comment`]="{ item }">
                    <span :title="item.comment">
                        {{ item.comment?.length > 50 ? item.comment.slice(0, 50) + '...' : item.comment }}
                    </span>
                </template>

                <!-- Пустой слот для данных -->
                <template v-slot:no-data>
                    <div class="text-center pa-4">
                        <v-icon large color="grey lighten-2">mdi-file-document-outline</v-icon>
                        <div class="text-subtitle-1 mt-2">Нет данных для отображения</div>
                    </div>
                </template>
            </v-data-table>

            <!-- Итоговая строка -->
            <v-row class="mt-4 px-3">
                <v-col cols="12" class="text-right">
                    <v-chip color="primary" outlined>
                        <strong>Всего записей: {{ verificationResultsDetails.length }}</strong>
                    </v-chip>
                    <v-chip color="success" outlined class="ml-2">
                        <v-icon left small>mdi-arrow-up-bold</v-icon>
                        <strong>Начислено: {{ formatNumber(totalAccrued) }} ₽</strong>
                    </v-chip>
                    <v-chip color="error" outlined class="ml-2">
                        <v-icon left small>mdi-arrow-down-bold</v-icon>
                        <strong>Списано: {{ formatNumber(totalDebited) }} ₽</strong>
                    </v-chip>
                    <v-chip 
                        :color="saldoEnd >= 0 ? 'info' : 'warning'" 
                        outlined 
                        class="ml-2"
                    >
                        <strong>Сальдо: {{ formatNumber(saldoEnd) }} ₽</strong>
                    </v-chip>
                </v-col>
            </v-row>

            <!-- Разделитель -->
            <v-divider class="my-4"></v-divider>

            <!-- Панель экспорта -->
            <v-row dense align="center" class="px-3">
                <v-col cols="auto">
                    <FileTypeSelector v-model="fileType" />
                </v-col>
                <v-col cols="auto">
                    <v-btn color="primary" :loading="exportLoading"
                        :disabled="!verificationResultsDetails.length || exportLoading" @click="exportClick">
                        <v-icon left>mdi-download</v-icon>
                        Экспортировать
                        <v-icon right v-if="fileType === 'pdf'">mdi-file-pdf</v-icon>
                        <v-icon right v-else>mdi-file-excel</v-icon>
                    </v-btn>
                </v-col>
                <v-col cols="auto" v-if="verificationResultsDetails.length">
                    <v-chip small color="info">
                        {{ verificationResultsDetails.length }} записей
                    </v-chip>
                </v-col>
                <v-col cols="auto" class="ml-auto">
                    <v-chip small color="grey" text-color="white">
                        <v-icon left small>mdi-calendar</v-icon>
                        {{ formatDate(dateFrom) }} — {{ formatDate(dateTo) }}
                    </v-chip>
                </v-col>
            </v-row>
        </v-card>
    </v-container>
</template>

<script>
import FileTypeSelector from '@/components/FileTypeSelector.vue';

export default {
    name: 'VerificationDetails',

    components: {
        FileTypeSelector
    },

    data() {
        return {
            verificationResultsDetails: [],
            headers: [
                { text: '№', value: 'npp', sortable: true },
                { text: 'Дата начисления', value: 'accrualDate', sortable: true },
                { text: 'Номер', value: 'number', sortable: true },
                { text: 'Сумма', value: 'summa', sortable: true },
                { text: 'Комментарий с расчёту', value: 'commentCalc', sortable: true },
                { text: 'Дата п/п', value: 'paymentOrderDate', sortable: true },
                { text: '№ п/п', value: 'paymentOrderNumber', sortable: true },
                { text: 'Сумма', value: 'paymentOrderAmount', sortable: true },
                { text: 'Комментарий', value: 'comment', sortable: false }
            ],
            dateFrom: null,
            dateTo: null,
            logisticCompanyName: '',
            serviceProducerName: '',
            saldoBeg: 0,
            inn: '',
            loading: false,
            exportLoading: false,
            fileType: "pdf",
            saldoEnd: 0,
            totalAccrued: 0,
            totalDebited: 0
        }
    },

    created() {
        this.loadVerificationResultsDetails();
    },

    methods: {
        formatNumber(value) {
            if (value === null || value === undefined || isNaN(value)) return '0.00';
            return Number(value).toFixed(2);
        },

        formatDate(dateString) {
            // Если значение null, undefined или пустое
            if (!dateString) return '';
            
            // Если это уже объект Date
            if (dateString instanceof Date) {
                if (isNaN(dateString.getTime())) return '';
                return dateString.toLocaleDateString('ru-RU');
            }

            // Если это строка
            if (typeof dateString === 'string') {
                // Проверяем на пустую строку или специальные значения
                if (!dateString.trim() || dateString === 'null' || dateString === 'undefined') {
                    return '';
                }

                // Проверяем, что строка соответствует формату dd.MM.yyyy
                const parts = dateString.split('.');
                if (parts.length === 3) {
                    const day = parseInt(parts[0], 10);
                    const month = parseInt(parts[1], 10) - 1;
                    const year = parseInt(parts[2], 10);

                    if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
                        const date = new Date(year, month, day);
                        if (!isNaN(date.getTime())) {
                            return date.toLocaleDateString('ru-RU');
                        }
                    }
                }

                // Пробуем стандартный парсинг
                try {
                    const date = new Date(dateString);
                    if (!isNaN(date.getTime())) {
                        return date.toLocaleDateString('ru-RU');
                    }
                } catch (e) {
                    // Игнорируем ошибку
                }

                return '';
            }

            // Для любых других типов (число, объект и т.д.)
            return '';
        },

        async loadVerificationResultsDetails() {
            try {
                this.loading = true;

                const { dateFrom, dateTo, logisticCompanyName, saldoBeg, inn } = this.$route.query;

                // Сохраняем для отображения в шапке
                this.dateFrom = dateFrom || '';
                this.dateTo = dateTo || '';
                this.logisticCompanyName = logisticCompanyName || '';
                this.saldoBeg = parseFloat(saldoBeg) || 0;
                this.inn = inn || '';

                const response = await this.axios.get(`/reports/VerificationDetails`, {
                    params: {
                        dateFrom,
                        dateTo,
                        logisticCompanyName,
                        saldoBeg: this.saldoBeg,
                        inn: this.inn
                    }
                });

                console.log('Full response:', response);
                console.log('response.data:', response.data);

                // Проверяем структуру ответа
                if (response.data) {
                    // Извлекаем данные из response.data
                    this.verificationResultsDetails = response.data.verificationDetailRowsWithParsedSums || [];
                    this.saldoEnd = response.data.saldoEnd || 0;
                    this.totalAccrued = response.data.totalAccrued || 0;
                    this.totalDebited = response.data.totalDebited || 0;
                    this.dateFrom = response.data.dateFrom || this.dateFrom;
                    this.dateTo = response.data.dateTo || this.dateTo;
                    this.logisticCompanyName = response.data.logisticCompanyName || this.logisticCompanyName;
                    this.serviceProducerName = response.data.serviceProducerName || '';

                    console.log('Total Accrued:', this.totalAccrued);
                    console.log('Total Debited:', this.totalDebited);
                    console.log('Saldo End:', this.saldoEnd);
                    console.log('Details count:', this.verificationResultsDetails.length);
                }

                this.loading = false;
            } catch (error) {
                console.error('Error loading verification details:', error);
                this.$notify(
                    'error',
                    error.response?.data?.message || 'Ошибка при загрузке деталей выверки'
                );
                this.loading = false;
            }
        },

        async exportClick() {
            try {
                // Проверка наличия данных
                if (!this.verificationResultsDetails || !this.verificationResultsDetails.length) {
                    this.$notify?.('warning', 'Нет данных для экспорта');
                    return;
                }

                this.exportLoading = true;

                // Формируем данные для экспорта
                const exportData = {
                    dateFrom: this.dateFrom,
                    dateTo: this.dateTo,
                    saldoBeg: this.saldoBeg,
                    saldoEnd: this.saldoEnd,
                    logisticCompanyName: this.logisticCompanyName,
                    serviceProducerName: this.serviceProducerName,
                    fileType: this.fileType,
                    verificationDetailsRows: this.verificationResultsDetails.map(item => ({
                        npp: item.npp,
                        accrualDate: item.accrualDate,
                        number: item.number,
                        summa: item.summa || 0,
                        commentCalc: item.commentCalc || '',
                        paymentOrderDate: item.paymentOrderDate,
                        paymentOrderNumber: item.paymentOrderNumber,
                        paymentOrderAmount: item.paymentOrderAmount || 0,
                        comment: item.comment || ''
                    }))
                };

                console.log('Export data:', exportData);

                // Отправляем POST запрос
                const response = await this.axios.post('/reports/VerificationDetails', exportData, {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                });

                // Обработка ответа (сервер возвращает URL)
                if (response.data) {
                    // Если пришла строка с URL
                    if (typeof response.data === 'string') {
                        window.open(response.data, '_blank');
                        this.$notify?.('success', 'Отчет успешно сформирован');
                    }
                    // Если пришел объект с URL
                    else if (response.data.url) {
                        window.open(response.data.url, '_blank');
                        this.$notify?.('success', 'Отчет успешно сформирован');
                    }
                    // Если пришел файл (blob)
                    else if (response.data instanceof Blob) {
                        const url = window.URL.createObjectURL(response.data);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = `Акт_сверки_${this.dateFrom.toISOString().slice(0,10)}_${this.dateTo.toISOString().slice(0,10)}.${this.fileType === 'pdf' ? 'pdf' : 'xlsx'}`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(url);
                        this.$notify?.('success', 'Отчет успешно скачан');
                    } else {
                        this.$notify?.('error', 'Не удалось получить ссылку на отчет');
                    }
                } else {
                    this.$notify?.('error', 'Не удалось получить ссылку на отчет');
                }

                this.exportLoading = false;

            } catch (error) {
                console.error('Export error:', error);
                this.$notify?.(
                    'error',
                    error.response?.data?.message || error.response?.data?.error || 'Ошибка при формировании отчёта'
                );
                this.exportLoading = false;
            }
        }
    }
}
</script>